"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState, useMemo } from "react";
import { LinkCard } from "@/components/ui/LinkCard";
import type { Job } from "@/features/personio/personio.types";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/buttons";
import { SlideUpIn } from "@/components/animations/SlideUpIn";
import { useRouter, useSearchParams } from "next/navigation";
import { Select } from "@/components/ui/form/Select";
import { useTranslations } from "@/lib/i18n/useTranslations";
export interface JobTeaserProps {
  /** The list of jobs to display */
  jobs: Job[];
  /** Optional additional class names */
  className?: string;
  /** Options for "Load More" functionality */
  loadMore?: {
    /** Initial number of items to show */
    initialCount: number;
    /** Number of items to add on each click */
    step: number;
    /** Label for the button */
    buttonLabel?: string;
  };
}

/**
 * JobTeaser Component
 * Renders a grid of job postings using the JobCard component.
 * Layout: 1 column on mobile, 2 columns on desktop.
 */
export const JobTeaser: React.FC<JobTeaserProps> = t0 => {
  const $ = _c(80);
  const {
    jobs,
    className,
    loadMore
  } = t0;
  const router = useRouter();
  const searchParams = useSearchParams();
  const t = useTranslations();
  let t1;
  if ($[0] !== searchParams) {
    t1 = searchParams.get("category") || "all";
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const filterCategory = t1;
  let t2;
  if ($[2] !== searchParams) {
    t2 = searchParams.get("type") || "all";
    $[2] = searchParams;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const filterEmploymentType = t2;
  const [extraVisibleCount, setExtraVisibleCount] = useState(0);
  let t3;
  if ($[4] !== filterCategory || $[5] !== filterEmploymentType) {
    t3 = {
      category: filterCategory,
      type: filterEmploymentType
    };
    $[4] = filterCategory;
    $[5] = filterEmploymentType;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const [prevFilters, setPrevFilters] = useState(t3);
  if (prevFilters.category !== filterCategory || prevFilters.type !== filterEmploymentType) {
    setPrevFilters({
      category: filterCategory,
      type: filterEmploymentType
    });
    setExtraVisibleCount(0);
  }
  let t4;
  if ($[7] !== jobs) {
    t4 = jobs || [];
    $[7] = jobs;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== filterCategory || $[10] !== filterEmploymentType || $[11] !== t4) {
    let t6;
    if ($[13] !== filterCategory || $[14] !== filterEmploymentType) {
      t6 = job => {
        const matchCategory = filterCategory === "all" || job.occupationCategoryRaw === filterCategory;
        const matchEmploymentType = filterEmploymentType === "all" || job.employmentTypeRaw === filterEmploymentType;
        return matchCategory && matchEmploymentType;
      };
      $[13] = filterCategory;
      $[14] = filterEmploymentType;
      $[15] = t6;
    } else {
      t6 = $[15];
    }
    t5 = t4.filter(t6);
    $[9] = filterCategory;
    $[10] = filterEmploymentType;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const filteredJobs = t5;
  const initialCount = loadMore?.initialCount ?? filteredJobs.length;
  const currentTotalVisible = Math.min(initialCount + extraVisibleCount, filteredJobs.length);
  let t6;
  if ($[16] !== currentTotalVisible || $[17] !== filteredJobs) {
    t6 = filteredJobs.slice(0, currentTotalVisible);
    $[16] = currentTotalVisible;
    $[17] = filteredJobs;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const displayedJobs = t6;
  const hasMore = currentTotalVisible < filteredJobs.length;
  let t7;
  if ($[19] !== loadMore) {
    t7 = () => {
      if (loadMore) {
        setExtraVisibleCount(prev => prev + loadMore.step);
      }
    };
    $[19] = loadMore;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const handleLoadMore = t7;
  let t8;
  if ($[21] !== router || $[22] !== searchParams) {
    t8 = (key, value) => {
      const newParams = new URLSearchParams(searchParams.toString());
      if (value === "all") {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
      router.push(`?${newParams.toString()}`, {
        scroll: false
      });
    };
    $[21] = router;
    $[22] = searchParams;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const updateFilters = t8;
  let categories;
  if ($[24] !== jobs) {
    categories = new Set();
    jobs.forEach(job_0 => {
      if (job_0.occupationCategoryRaw) {
        categories.add(job_0.occupationCategoryRaw);
      }
    });
    $[24] = jobs;
    $[25] = categories;
  } else {
    categories = $[25];
  }
  let t9;
  if ($[26] !== t.jobs.labels.filter.all) {
    t9 = {
      label: t.jobs.labels.filter.all,
      value: "all"
    };
    $[26] = t.jobs.labels.filter.all;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== categories) {
    t10 = Array.from(categories);
    $[28] = categories;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== t.jobs.labels.occupationCategory || $[31] !== t10) {
    t11 = t10.map(cat => ({
      label: t.jobs.labels.occupationCategory[cat as keyof typeof t.jobs.labels.occupationCategory] || cat,
      value: cat
    }));
    $[30] = t.jobs.labels.occupationCategory;
    $[31] = t10;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== t11 || $[34] !== t9) {
    t12 = [t9, ...t11];
    $[33] = t11;
    $[34] = t9;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  const categoryOptions = t12;
  let types;
  if ($[36] !== jobs) {
    types = new Set();
    jobs.forEach(job_1 => {
      if (job_1.employmentTypeRaw) {
        types.add(job_1.employmentTypeRaw);
      }
    });
    $[36] = jobs;
    $[37] = types;
  } else {
    types = $[37];
  }
  let t13;
  if ($[38] !== t.jobs.labels.filter.all) {
    t13 = {
      label: t.jobs.labels.filter.all,
      value: "all"
    };
    $[38] = t.jobs.labels.filter.all;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== types) {
    t14 = Array.from(types);
    $[40] = types;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== t.jobs.labels.employmentType || $[43] !== t14) {
    t15 = t14.map(type => ({
      label: t.jobs.labels.employmentType[type as keyof typeof t.jobs.labels.employmentType] || type,
      value: type
    }));
    $[42] = t.jobs.labels.employmentType;
    $[43] = t14;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] !== t13 || $[46] !== t15) {
    t16 = [t13, ...t15];
    $[45] = t13;
    $[46] = t15;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  const employmentTypeOptions = t16;
  if (!jobs || jobs.length === 0) {
    return null;
  }
  let t17;
  if ($[48] !== className) {
    t17 = cn("container", className);
    $[48] = className;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  let t18;
  if ($[50] !== updateFilters) {
    t18 = e => updateFilters("category", e.target.value);
    $[50] = updateFilters;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  let t19;
  if ($[52] !== categoryOptions || $[53] !== filterCategory || $[54] !== t.jobs.labels.filter.occupationCategory || $[55] !== t18) {
    t19 = <div className="w-full md:w-1/2"><Select color="dark" label={t.jobs.labels.filter.occupationCategory} value={filterCategory} onChange={t18} options={categoryOptions} id="occupationCategory" /></div>;
    $[52] = categoryOptions;
    $[53] = filterCategory;
    $[54] = t.jobs.labels.filter.occupationCategory;
    $[55] = t18;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  let t20;
  if ($[57] !== updateFilters) {
    t20 = e_0 => updateFilters("type", e_0.target.value);
    $[57] = updateFilters;
    $[58] = t20;
  } else {
    t20 = $[58];
  }
  let t21;
  if ($[59] !== employmentTypeOptions || $[60] !== filterEmploymentType || $[61] !== t.jobs.labels.filter.employmentType || $[62] !== t20) {
    t21 = <div className="w-full md:w-1/2"><Select color="dark" label={t.jobs.labels.filter.employmentType} value={filterEmploymentType} onChange={t20} options={employmentTypeOptions} id="employmentType" /></div>;
    $[59] = employmentTypeOptions;
    $[60] = filterEmploymentType;
    $[61] = t.jobs.labels.filter.employmentType;
    $[62] = t20;
    $[63] = t21;
  } else {
    t21 = $[63];
  }
  let t22;
  if ($[64] !== t19 || $[65] !== t21) {
    t22 = <div className="col-span-full flex flex-col md:flex-row gap-md mb-xl">{t19}{t21}</div>;
    $[64] = t19;
    $[65] = t21;
    $[66] = t22;
  } else {
    t22 = $[66];
  }
  let t23;
  if ($[67] !== displayedJobs || $[68] !== filteredJobs.length || $[69] !== t.jobs.labels.filter.noJobsFound) {
    t23 = filteredJobs.length > 0 ? <div className="col-span-full grid grid-cols-1 gap-md auto-rows-fr md:grid-cols-2">{displayedJobs.map(_temp)}</div> : <div className="col-span-full md:col-span-9 text-white headline-3">{t.jobs.labels.filter.noJobsFound}</div>;
    $[67] = displayedJobs;
    $[68] = filteredJobs.length;
    $[69] = t.jobs.labels.filter.noJobsFound;
    $[70] = t23;
  } else {
    t23 = $[70];
  }
  let t24;
  if ($[71] !== handleLoadMore || $[72] !== hasMore || $[73] !== loadMore) {
    t24 = loadMore && hasMore && <div className="flex justify-center mt-xl col-span-full"><Button variant="primary" color="yellow" label={loadMore.buttonLabel} icon="arrow-right" onClick={handleLoadMore} /></div>;
    $[71] = handleLoadMore;
    $[72] = hasMore;
    $[73] = loadMore;
    $[74] = t24;
  } else {
    t24 = $[74];
  }
  let t25;
  if ($[75] !== t17 || $[76] !== t22 || $[77] !== t23 || $[78] !== t24) {
    t25 = <div className={t17}>{t22}{t23}{t24}</div>;
    $[75] = t17;
    $[76] = t22;
    $[77] = t23;
    $[78] = t24;
    $[79] = t25;
  } else {
    t25 = $[79];
  }
  return t25;
};
function _temp(job_2) {
  return <SlideUpIn key={job_2.id}><LinkCard title={job_2.title} tags={[{
      icon: "location",
      label: job_2.location
    }, {
      icon: "time",
      label: job_2.schedule
    }, {
      icon: "hyperlink",
      label: job_2.employmentType
    }]} link={job_2.link} className="h-full" /></SlideUpIn>;
}